// §34.6 / D-496 — "files over `media.syncThresholdMb` stay **device-local**: synced never".
//
// The whole of the rule, in one pure function, because it is consulted from three places that must
// not be allowed to disagree: the sync client (which skips the push), Explorer (which badges the
// file "on this device"), and the tests. Two implementations of "is this file too big to sync"
// would eventually badge a file the sync layer was happily uploading, which is worse than either
// behaviour on its own.
//
// **It lives in `shared/` because both halves need it and they are on opposite sides of the import
// wall.** §6.5 forbids `apps/**` importing `kernel/**`, so a predicate the sync client and Explorer
// both consult cannot live in `kernel/vfs/` — which is where it was first written, until
// `boundaries.test.ts` said so. `shared/` is the package that exists for exactly this.
//
// **Why a threshold at all** (D-496): the sync path was designed against documents — D-047 blobs
// hashed whole, pushed whole, and held in the server's blob store. A screen recording is a
// different kind of object, and the honest answer for one is that it lives where it was made. The
// threshold is a §14 setting rather than a constant so it is visible, searchable and adjustable
// instead of being a number somebody has to read the source to discover.

/** Megabytes as §14 states them, in the bytes the filesystem actually reports. */
const BYTES_PER_MB = 1_048_576;

/**
 * Is a file of `sizeBytes` device-local at a threshold of `thresholdMb`?
 *
 * **Strictly greater than**, matching §34.6's "files *over* the threshold". A file of exactly
 * 256 MB syncs, and that is the boundary the test pins — an off-by-one here is a file that silently
 * stops syncing at the round number a user is most likely to test with.
 *
 * A non-positive or non-finite threshold means **no threshold**: nothing is device-local. That is
 * the safe direction for a setting a user can type into. The alternative reading — zero means
 * "sync nothing" — would turn a mistyped setting into a device that quietly stops backing anything
 * up, which is the failure mode you discover by losing a file.
 */
export function isDeviceLocal(sizeBytes: number, thresholdMb: number): boolean {
  if (!Number.isFinite(thresholdMb) || thresholdMb <= 0) return false;
  if (!Number.isFinite(sizeBytes) || sizeBytes < 0) return false;
  return sizeBytes > thresholdMb * BYTES_PER_MB;
}

/** The threshold in bytes, for a caller that wants to compare sizes itself. */
export function deviceLocalBytes(thresholdMb: number): number {
  return thresholdMb * BYTES_PER_MB;
}
